// src/pages/hotspot-plans/HotspotPlansPage.tsx
"use client";

import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  Banknote,
  Clock,
  Database,
  Edit2,
  Loader2,
  Package,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Users,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { fetchData } from "../../services/apiService";

interface HotspotPlan {
  id: number;
  client_id: number;
  name: string;
  price: number;
  currency: string;
  upload_speed: number;
  download_speed: number;
  duration_minutes: number;
  shared_users: number;
  data_limit_mb?: number | null;
  description?: string | null;
}

interface PlanFormData {
  name: string;
  price: string;
  upload_speed_mbps: string;
  download_speed_mbps: string;
  duration_hours: string;
  shared_users: string;
  data_limit_mb: string;
  description: string;
}

const emptyForm: PlanFormData = {
  name: "",
  price: "",
  upload_speed_mbps: "",
  download_speed_mbps: "",
  duration_hours: "1",
  shared_users: "1",
  data_limit_mb: "",
  description: "",
};

const normalizeSpeedToMbps = (speed: number) => {
  return speed > 500 ? speed / 1024 : speed;
};

const toKbps = (mbps: number) => mbps * 1024;

const fmtKES = (value: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(Number(value || 0))}`;

const fmtDuration = (minutes: number) => {
  if (minutes >= 1440) {
    const days = minutes / 1440;
    return `${days} ${days === 1 ? "Day" : "Days"}`;
  }

  const hours = minutes / 60;

  return Number.isInteger(hours)
    ? `${hours} ${hours === 1 ? "Hour" : "Hours"}`
    : `${minutes} Mins`;
};

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textAreaCls =
  "min-h-[96px] w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-2.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: React.ReactNode;
}> = ({ label, required, children, hint }) => (
  <div className="flex flex-col gap-1.5">
    <label className="flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-400">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </label>

    {children}

    {hint && (
      <div className="text-[11.5px] font-medium leading-snug text-slate-500">
        {hint}
      </div>
    )}
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const SpeedBlock: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: "emerald" | "sky";
}> = ({ label, value, icon, tone }) => {
  const cls =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-sky-200 bg-sky-50 text-sky-700";

  return (
    <div className={`rounded-2xl border px-3 py-3 ${cls}`}>
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/70">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest opacity-80">
            {label}
          </p>

          <p className="truncate text-[13px] font-black text-slate-900">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
};

const HotspotPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<HotspotPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<HotspotPlan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    plan: HotspotPlan | null;
  }>({
    open: false,
    plan: null,
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchData<HotspotPlan[]>("/hotspot-plans");

      setPlans(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to load hotspot plans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim() || !formData.price.trim()) {
      setError("Name and price are required.");
      return;
    }

    if (
      !formData.download_speed_mbps.trim() ||
      !formData.upload_speed_mbps.trim()
    ) {
      setError("Download and upload speeds are required.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: formData.name.trim(),
        price: Number(formData.price),
        upload_speed: toKbps(Number(formData.upload_speed_mbps)),
        download_speed: toKbps(Number(formData.download_speed_mbps)),
        duration_minutes: Number(formData.duration_hours) * 60,
        shared_users: Number(formData.shared_users),
        data_limit_mb: formData.data_limit_mb
          ? Number(formData.data_limit_mb)
          : null,
        description: formData.description.trim() || null,
      };

      if (editingPlan) {
        await fetchData(`/hotspot-plans/${editingPlan.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await fetchData("/hotspot-plans", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      await fetchPlans();
      closeDrawer();
    } catch (err: any) {
      setError(err?.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (plan: HotspotPlan) => {
    setDeleteDialog({ open: true, plan });
  };

  const handleExecuteDelete = async () => {
    if (!deleteDialog.plan) return;

    setIsDeleting(true);

    try {
      await fetchData(`/hotspot-plans/${deleteDialog.plan.id}`, {
        method: "DELETE",
      });

      setPlans((prev) => prev.filter((p) => p.id !== deleteDialog.plan?.id));
      setDeleteDialog({ open: false, plan: null });
    } catch (err: any) {
      setError(err?.message || "Failed to delete plan.");
    } finally {
      setIsDeleting(false);
    }
  };

  const openCreate = () => {
    setEditingPlan(null);
    setFormData(emptyForm);
    setDrawerOpen(true);
    setError(null);
  };

  const openEdit = (plan: HotspotPlan) => {
    setEditingPlan(plan);

    setFormData({
      name: plan.name,
      price: String(plan.price),
      upload_speed_mbps: String(normalizeSpeedToMbps(plan.upload_speed)),
      download_speed_mbps: String(normalizeSpeedToMbps(plan.download_speed)),
      duration_hours: String(
        (plan.duration_minutes / 60).toFixed(1).replace(/\.0$/, ""),
      ),
      shared_users: String(plan.shared_users),
      data_limit_mb: plan.data_limit_mb ? String(plan.data_limit_mb) : "",
      description: plan.description || "",
    });

    setDrawerOpen(true);
    setError(null);
  };

  const closeDrawer = () => {
    if (saving) return;

    setDrawerOpen(false);

    setTimeout(() => {
      setEditingPlan(null);
      setFormData(emptyForm);
      setError(null);
    }, 200);
  };

  const filteredPlans = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return plans;

    return plans.filter((p) => {
      return (
        p.name.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        String(p.price).includes(q) ||
        String(p.duration_minutes).includes(q)
      );
    });
  }, [plans, searchQuery]);

  const stats = useMemo(() => {
    const avgPrice =
      plans.length > 0
        ? Math.round(
            plans.reduce((sum, p) => sum + Number(p.price || 0), 0) /
              plans.length,
          )
        : 0;

    const unlimited = plans.filter((p) => !p.data_limit_mb).length;

    const avgDuration =
      plans.length > 0
        ? Math.round(
            plans.reduce((sum, p) => sum + Number(p.duration_minutes || 0), 0) /
              plans.length,
          )
        : 0;

    return {
      total: plans.length,
      avgPrice,
      unlimited,
      avgDuration,
    };
  }, [plans]);

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Hotspot Voucher Plans
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Hotspot Billing Plans
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Configure prices, speed limits, duration, data caps, and
                  shared device limits for WiFi vouchers.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={fetchPlans}
                  disabled={loading}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh
                </button>

                <button
                  type="button"
                  onClick={openCreate}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  New Plan
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          <StatCard
            label="Total Plans"
            value={stats.total}
            sub="Available voucher packages"
            icon={<Package className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Average Price"
            value={stats.avgPrice ? fmtKES(stats.avgPrice) : "—"}
            sub="Across all hotspot plans"
            icon={<Banknote className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Unlimited Plans"
            value={stats.unlimited}
            sub="No data cap configured"
            icon={<Database className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Average Duration"
            value={stats.avgDuration ? fmtDuration(stats.avgDuration) : "—"}
            sub="Typical voucher validity"
            icon={<Clock className="h-5 w-5" />}
            tone="amber"
          />
        </div>

        {/* Toolbar */}
        <div className="rounded-3xl border border-slate-200 bg-slate-50/95 p-3 shadow-lg shadow-slate-300/25 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                placeholder="Search plans by name, description, price..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${inputCls} pl-10`}
              />
            </div>

            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
            </div>

            <p className="text-[13px] font-bold text-slate-500">
              Loading hotspot plans…
            </p>
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 px-4 py-12 text-center shadow-xl shadow-slate-300/30">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-200">
              <Wifi className="h-8 w-8 text-slate-500" />
            </div>

            <p className="text-[15px] font-black text-slate-800">
              {searchQuery ? "No matching hotspot plans" : "No hotspot plans"}
            </p>

            <p className="mt-1 max-w-sm text-[13px] font-medium text-slate-500">
              {searchQuery
                ? "Try adjusting your search terms."
                : "Create your first voucher plan to start selling hotspot access."}
            </p>

            {!searchQuery && (
              <button
                type="button"
                onClick={openCreate}
                className={`${btnBase} mt-5 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                <Plus className="h-4 w-4" />
                Create Plan
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredPlans.map((plan) => {
              const download = normalizeSpeedToMbps(plan.download_speed);
              const upload = normalizeSpeedToMbps(plan.upload_speed);

              return (
                <div
                  key={plan.id}
                  className="group overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-300/35"
                >
                  <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-[16px] font-black text-slate-900">
                          {plan.name}
                        </h3>

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-[12px] font-black text-emerald-800">
                            {fmtKES(plan.price)}
                          </span>

                          <span className="rounded-full border border-sky-200 bg-sky-100 px-3 py-1 text-[12px] font-black text-sky-800">
                            {fmtDuration(plan.duration_minutes)}
                          </span>
                        </div>
                      </div>

                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/20">
                        <Wifi className="h-5 w-5" />
                      </div>
                    </div>

                    {plan.description && (
                      <p className="mt-3 line-clamp-2 text-[12.5px] font-medium leading-relaxed text-slate-500">
                        {plan.description}
                      </p>
                    )}

                    <div className="mt-4 grid grid-cols-1 gap-2.5 min-[380px]:grid-cols-2">
                      <SpeedBlock
                        label="Download"
                        value={`${download} Mbps`}
                        tone="emerald"
                        icon={<ArrowDownCircle className="h-4 w-4" />}
                      />

                      <SpeedBlock
                        label="Upload"
                        value={`${upload} Mbps`}
                        tone="sky"
                        icon={<ArrowUpCircle className="h-4 w-4" />}
                      />
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2.5">
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3">
                        <div className="flex items-center gap-2">
                          <Database className="h-4 w-4 shrink-0 text-amber-700" />

                          <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                              Data
                            </p>

                            <p className="truncate text-[12.5px] font-black text-slate-900">
                              {plan.data_limit_mb
                                ? `${plan.data_limit_mb} MB`
                                : "Unlimited"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-3">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 shrink-0 text-violet-700" />

                          <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-widest text-violet-700">
                              Devices
                            </p>

                            <p className="truncate text-[12.5px] font-black text-slate-900">
                              {plan.shared_users}{" "}
                              {plan.shared_users === 1 ? "User" : "Users"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 flex gap-2 border-t border-slate-200 pt-4">
                      <button
                        type="button"
                        onClick={() => openEdit(plan)}
                        className={`${btnBase} h-10 flex-1 border border-slate-300 bg-slate-50 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => confirmDelete(plan)}
                        className={`${btnBase} h-10 w-11 border border-rose-200 bg-rose-50 text-rose-600 shadow-sm hover:bg-rose-100`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 px-0 py-0 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6">
          <div className="flex h-[100svh] w-full flex-col overflow-hidden bg-slate-50 shadow-2xl shadow-slate-950/30 sm:max-h-[92vh] sm:max-w-3xl sm:rounded-3xl sm:border sm:border-slate-200">
            <div className="h-1.5 w-full shrink-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

            <div className="flex shrink-0 items-start justify-between border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-6">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <ShieldCheck className="h-5 w-5" />
                </div>

                <div>
                  <h2 className="text-[17px] font-black text-slate-900">
                    {editingPlan ? "Edit Hotspot Plan" : "Create Hotspot Plan"}
                  </h2>

                  <p className="mt-0.5 text-[12.5px] font-medium text-slate-500">
                    Set voucher pricing, speed, duration, devices, and data cap.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeDrawer}
                disabled={saving}
                className="rounded-2xl p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={handleSave}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Plan Name" required>
                    <input
                      required
                      className={inputCls}
                      placeholder="e.g. Daily Unlimited"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </Field>

                  <Field label="Price (KES)" required>
                    <input
                      required
                      type="number"
                      min="1"
                      className={inputCls}
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      placeholder="e.g. 100"
                    />
                  </Field>

                  <Field label="Duration (Hours)" required>
                    <input
                      required
                      type="number"
                      min="0.1"
                      step="0.1"
                      className={inputCls}
                      value={formData.duration_hours}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          duration_hours: e.target.value,
                        })
                      }
                      placeholder="e.g. 1"
                    />
                  </Field>

                  <Field label="Max Devices" required>
                    <input
                      required
                      type="number"
                      min="1"
                      className={inputCls}
                      value={formData.shared_users}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shared_users: e.target.value,
                        })
                      }
                      placeholder="e.g. 1"
                    />
                  </Field>

                  <div className="rounded-3xl border border-slate-200 bg-slate-100/80 p-4 sm:col-span-2">
                    <div className="mb-4 flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                        <Zap className="h-4 w-4" />
                      </div>

                      <div>
                        <p className="text-[13.5px] font-black text-slate-900">
                          Speed Limits
                        </p>

                        <p className="text-[11.5px] font-medium text-slate-500">
                          Enter speeds in Mbps. The system will send Kbps to the
                          backend.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Download (Mbps)" required>
                        <input
                          required
                          type="number"
                          min="0"
                          step="0.1"
                          className={inputCls}
                          placeholder="e.g. 5"
                          value={formData.download_speed_mbps}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              download_speed_mbps: e.target.value,
                            })
                          }
                        />
                      </Field>

                      <Field label="Upload (Mbps)" required>
                        <input
                          required
                          type="number"
                          min="0"
                          step="0.1"
                          className={inputCls}
                          placeholder="e.g. 2"
                          value={formData.upload_speed_mbps}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              upload_speed_mbps: e.target.value,
                            })
                          }
                        />
                      </Field>
                    </div>
                  </div>

                  <Field label="Data Limit (MB)">
                    <input
                      type="number"
                      min="0"
                      placeholder="Leave blank for unlimited"
                      className={inputCls}
                      value={formData.data_limit_mb}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          data_limit_mb: e.target.value,
                        })
                      }
                    />
                  </Field>

                  <Field label="Description">
                    <textarea
                      className={textAreaCls}
                      rows={3}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Optional description for this hotspot plan"
                    />
                  </Field>
                </div>
              </div>

              <div className="shrink-0 border-t border-slate-200 bg-slate-100/90 px-4 py-4 sm:px-6">
                <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    disabled={saving}
                    className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        {editingPlan ? "Update Plan" : "Save Plan"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteDialog.open}
        loading={isDeleting}
        title="Delete Plan?"
        description={`Are you sure you want to delete "${deleteDialog.plan?.name}"? This action cannot be undone and might affect users currently on this plan.`}
        confirmLabel="Yes, Delete"
        variant="danger"
        onCancel={() => setDeleteDialog({ open: false, plan: null })}
        onConfirm={handleExecuteDelete}
      />
    </div>
  );
};

export default HotspotPlansPage;
